import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { NoData } from 'pages/Asset/tabs/Cases/components/NoData/NoData';
import { useGetAllActionsQuery } from 'queries/cases/queries';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SingleAction } from '../SingleAction';
import CaseFormStep from '../../CaseFormStep/CaseFormStep';
import { CaseForm, caseStatus } from 'queries/cases/types';
import {
  GenericModal,
  GenericModalProps,
  ModalAlignBtnEnum,
  ModalWidthEnum,
} from 'ui/GenericModal/GenericModal';

import { useModal } from '@shape-ds/components';
import { createNewAction, isDiagnosisFilled } from './ActionsCardHelper';

interface ActionsCardProps {
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
  isFormDisabled: boolean;
}
export function ActionsCard({ onSubmit, isFormDisabled }: ActionsCardProps) {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const { formState, control, watch, getValues, setValue, trigger } = useFormContext();
  const { isFetching } = useGetAllActionsQuery(formState?.defaultValues?.id);

  const fieldArray = useFieldArray({
    control,
    name: 'actions',
  });

  const actions: CaseForm['actions'] = watch('actions') || [];

  const currentStatus = getValues('status');

  const handleChangeStatus = () => {
    setValue('status', caseStatus.actions);
    createNewAction(fieldArray);
    onSubmit();
    closeModal();
  };

  const diagnosisToActionsModalProps: GenericModalProps = {
    title: t('case:messages.diagnosis_to_actions.title'),
    description: t('case:messages.diagnosis_to_actions.message'),
    leftBtn: {
      style: 'secondary',
      text: t('case:messages.diagnosis_to_actions.button.cancel'),
      onClick: closeModal,
    },
    rightBtn: {
      style: 'primary',
      text: t('case:messages.diagnosis_to_actions.button.go_to_actions'),
      onClick: () => {
        handleChangeStatus();
      },
    },
  };

  const diagnosisNotFilledModalProps: GenericModalProps = {
    title: t('case:messages.diagnosis_not_filled.title'),
    description: t('case:messages.diagnosis_not_filled.message'),
    rightBtn: {
      style: 'primary',
      text: t('case:messages.diagnosis_not_filled.button.ok'),
      onClick: () => {
        trigger();
        closeModal();
      },
    },
  };

  const handleClick = (): void => {
    if (currentStatus === caseStatus.diagnosis) {
      const modalProps = isDiagnosisFilled(getValues)
        ? diagnosisToActionsModalProps
        : diagnosisNotFilledModalProps;
      openModal(
        <GenericModal
          {...modalProps}
          width={ModalWidthEnum.MEDIUM}
          alignBtnTo={ModalAlignBtnEnum.RIGHT}
        />,
        {
          className: 'actions-card-modal',
          PaperProps: {
            style: {
              background: 'var(--gray-1)',
              borderRadius: '8px',
            },
          },
        },
      );
    } else {
      createNewAction(fieldArray);
    }
  };

  return (
    <CaseFormStep
      stepName="actions"
      title={t('case:title.actions', { quantity: actions.length })}
      formComponent={({ isStepAble, isStepRequired }) => {
        return actions.length > 0 ? (
          actions.map((field, index) => (
            <SingleAction
              key={field.id}
              index={index}
              isStepAble={isStepAble}
              isStepRequired={isStepRequired}
              onSubmit={onSubmit}
            />
          ))
        ) : (
          <NoData icon={null} i18nKey="case:no_data.empty_actions" isLoading={isFetching} />
        );
      }}
      button={{
        icon: <AddCircleOutlineIcon />,
        onClick: handleClick,
        text: t('case:buttons.newAction'),
      }}
      isFormDisabled={isFormDisabled}
      onSubmit={onSubmit}
    />
  );
}
